/**
 * AUI Files Test Page (dev only)
 *
 * Visit `/dev/aui-files-test` to render the `AuiFiles` surface
 * component against a static in-memory file fixture. Stubs both the
 * platform-events context (no live WS) and the VFS REST endpoints
 * (intercepted via `fetch` override) so the page runs without a
 * backend.
 */

import { useEffect, useMemo, useState } from 'react';
import { PlatformEventContext } from '@/contexts/PlatformEventContext';
import type { PlatformEvent } from '@/types/events';
import { AuiNodeRenderer } from '@/components/aui/AuiNodeRenderer';
import type { AuiNode } from '@/types/canvases';
import type { VfsEntry } from '@/types/vfs';

const FIXTURE_OWNER = 'con-fixture';

const FIXTURE: VfsEntry[] = [
  { fileId: 'fil-readme',  ownerId: FIXTURE_OWNER, path: 'README.md',                    name: 'README.md',     size: 1842,  contentType: 'text/markdown',     createdAt: '2026-05-20T10:00:00Z', updatedAt: '2026-05-23T09:00:00Z', createdBy: 'agent' },
  { fileId: 'fil-config',  ownerId: FIXTURE_OWNER, path: 'config.json',                  name: 'config.json',   size: 412,   contentType: 'application/json',  createdAt: '2026-05-20T10:01:00Z', updatedAt: '2026-05-20T10:01:00Z', createdBy: 'user'  },
  { fileId: 'fil-spec',    ownerId: FIXTURE_OWNER, path: 'docs/spec.md',                 name: 'spec.md',       size: 8421,  contentType: 'text/markdown',     createdAt: '2026-05-21T14:00:00Z', updatedAt: '2026-05-23T08:00:00Z', createdBy: 'agent' },
  { fileId: 'fil-notes',   ownerId: FIXTURE_OWNER, path: 'docs/notes.txt',               name: 'notes.txt',     size: 240,   contentType: 'text/plain',        createdAt: '2026-05-22T11:00:00Z', updatedAt: '2026-05-22T11:00:00Z', createdBy: 'agent' },
  { fileId: 'fil-chart',   ownerId: FIXTURE_OWNER, path: 'reports/q1/chart.png',         name: 'chart.png',     size: 88234, contentType: 'image/png',         createdAt: '2026-05-22T15:00:00Z', updatedAt: '2026-05-22T15:00:00Z', createdBy: 'agent' },
  { fileId: 'fil-data',    ownerId: FIXTURE_OWNER, path: 'reports/q1/data.csv',          name: 'data.csv',      size: 12480, contentType: 'text/csv',          createdAt: '2026-05-22T15:01:00Z', updatedAt: '2026-05-22T15:01:00Z', createdBy: 'user'  },
  { fileId: 'fil-wide',    ownerId: FIXTURE_OWNER, path: 'reports/q1/perf_deltas.csv',   name: 'perf_deltas.csv', size: 8800, contentType: 'text/csv',          createdAt: '2026-05-22T15:02:00Z', updatedAt: '2026-05-22T15:02:00Z', createdBy: 'agent' },
  { fileId: 'fil-build',   ownerId: FIXTURE_OWNER, path: 'scripts/build.sh',             name: 'build.sh',      size: 312,   contentType: 'application/x-sh', createdAt: '2026-05-19T08:00:00Z', updatedAt: '2026-05-19T08:00:00Z', createdBy: 'agent' },
  { fileId: 'fil-deploy',  ownerId: FIXTURE_OWNER, path: 'scripts/deploy.sh',            name: 'deploy.sh',     size: 540,   contentType: 'application/x-sh', createdAt: '2026-05-19T08:01:00Z', updatedAt: '2026-05-19T08:01:00Z', createdBy: 'agent' },
  { fileId: 'fil-import',  ownerId: FIXTURE_OWNER, path: 'scripts/import.js',            name: 'import.js',     size: 1762,  contentType: 'application/x-javascript', createdAt: '2026-05-19T08:02:00Z', updatedAt: '2026-05-19T08:02:00Z', createdBy: 'agent' },
  { fileId: 'fil-pipe',    ownerId: FIXTURE_OWNER, path: 'scripts/pipeline.py',          name: 'pipeline.py',   size: 612,   contentType: 'text/x-python',    createdAt: '2026-05-19T08:03:00Z', updatedAt: '2026-05-19T08:03:00Z', createdBy: 'agent' },
  { fileId: 'fil-sql',     ownerId: FIXTURE_OWNER, path: 'scripts/seed.sql',             name: 'seed.sql',      size: 421,   contentType: 'application/sql',  createdAt: '2026-05-19T08:04:00Z', updatedAt: '2026-05-19T08:04:00Z', createdBy: 'agent' },
];

// ─── Lazy KB fixture (kbs-*) ──────────────────────────────────────────
// Exercises the lazy per-folder backend: `kbApi.listNodes` is stubbed to
// return one folder's direct children at a time, paginated. Covers a normal
// folder, a large (250-child) folder that paginates + virtualizes, an empty
// folder, and a folder that errors.
const KB_OWNER = 'kbs-fixture';

function kbFolder(path: string) {
  return {
    kbId: KB_OWNER,
    path,
    name: path.split('/').pop(),
    kind: 'folder' as const,
    createdAt: '2026-05-01T00:00:00Z',
    updatedAt: '2026-05-01T00:00:00Z',
  };
}
function kbFile(path: string, i: number) {
  return {
    kbId: KB_OWNER,
    path,
    name: path.split('/').pop(),
    kind: 'file' as const,
    fileId: `doc-${path}`,
    sizeBytes: 1024 + i * 7,
    contentType: 'text/markdown',
    indexStatus: i % 5 === 0 ? 'pending' : 'indexed',
    createdAt: '2026-05-01T00:00:00Z',
    updatedAt: '2026-05-02T00:00:00Z',
  };
}

// Folder path ('' = root) → its direct children, in server order. 'broken' is
// intentionally absent so the stub returns 500 for it (error sentinel).
const KB_TREE: Record<string, ReturnType<typeof kbFile | typeof kbFolder>[]> = {
  '': [
    kbFolder('docs'),
    kbFolder('big'),
    kbFolder('empty'),
    kbFolder('broken'),
    kbFile('overview.md', 1),
    kbFile('license.txt', 2),
  ],
  docs: [kbFile('docs/architecture.md', 1), kbFile('docs/api.md', 2), kbFile('docs/faq.md', 3)],
  big: Array.from({ length: 250 }, (_, i) => kbFile(`big/file-${String(i).padStart(3, '0')}.md`, i)),
  empty: [],
};

function kbNodesResponse(url: string): Response | null {
  const m = url.match(/\/api\/v1\/kb\/([^/?#]+)\/nodes/);
  if (!m || decodeURIComponent(m[1]) !== KB_OWNER) return null;
  const u = new URL(url, 'http://stub');
  const path = u.searchParams.get('path') ?? '';
  const limit = Number(u.searchParams.get('limit') ?? '100');
  const cursor = Number(u.searchParams.get('cursor') ?? '0');
  const json = (body: unknown, status = 200) =>
    new Response(JSON.stringify(body), { status, headers: { 'Content-Type': 'application/json' } });
  if (path === 'broken') return json({ message: 'Simulated listing failure' }, 500);
  const all = KB_TREE[path];
  if (!all) return json({ data: [], pagination: { count: 0, nextCursor: null, hasMore: false } });
  const slice = all.slice(cursor, cursor + limit);
  const nextOffset = cursor + limit;
  const hasMore = nextOffset < all.length;
  return json({
    data: slice,
    pagination: { count: slice.length, nextCursor: hasMore ? String(nextOffset) : null, hasMore },
  });
}

// ─── Skill files fixture (skl-*) ──────────────────────────────────────
// Exercises the dedicated skill-files backend (`/api/v1/skills/{id}/files`):
// flat list of SkillFileRefs (no fileId), base64 upload, presigned get,
// cascade delete, move. No WS events — the adapter's optimistic store drives
// the tree. Mutations below patch SKILL_FILES so a refetch stays consistent.
const SKILL_OWNER = 'skl-fixture';

interface SkillFileRefFixture {
  path: string;
  contentType: string;
  sizeBytes: number;
  checksum: string;
}

const SKILL_FILES: SkillFileRefFixture[] = [
  { path: 'SKILL.md', contentType: 'text/markdown', sizeBytes: 1240, checksum: 'sk-readme' },
  { path: 'queries/top_customers.sql', contentType: 'application/sql', sizeBytes: 612, checksum: 'sk-sql1' },
  { path: 'queries/revenue_by_region.sql', contentType: 'application/sql', sizeBytes: 814, checksum: 'sk-sql2' },
  { path: 'scripts/sync.py', contentType: 'text/x-python', sizeBytes: 1530, checksum: 'sk-py' },
  { path: 'docs/usage.md', contentType: 'text/markdown', sizeBytes: 980, checksum: 'sk-usage' },
];

const SKILL_BODIES: Record<string, { contentType: string; body: string }> = {
  'SKILL.md': {
    contentType: 'text/markdown',
    body: ['# Sales Insights Skill', '', 'Run the SQL in `queries/` against the warehouse and', 'summarize the results.', ''].join('\n'),
  },
  'queries/top_customers.sql': {
    contentType: 'application/sql',
    body: ['SELECT customer, SUM(revenue) AS total', 'FROM orders', 'GROUP BY customer', 'ORDER BY total DESC', 'LIMIT 10;', ''].join('\n'),
  },
};

function jsonResp(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

// Handle every `/api/v1/skills/{SKILL_OWNER}/files...` request from the
// in-memory fixture. Returns null for non-skill URLs so the caller falls
// through to the VFS/KB branches.
function skillFilesResponse(url: string, init?: RequestInit): Response | null {
  const base = `/api/v1/skills/${SKILL_OWNER}/files`;
  if (!url.includes(`/api/v1/skills/${SKILL_OWNER}/files`)) return null;
  const method = init?.method ?? 'GET';

  // POST .../files/move
  if (method === 'POST' && url.endsWith('/files/move')) {
    const body = JSON.parse(typeof init?.body === 'string' ? init.body : '{}') as {
      from?: string;
      to?: string;
      overwrite?: boolean;
    };
    if (!body.from || !body.to) return jsonResp({ error: 'from/to required', code: 'BAD_REQUEST' }, 400);
    const exists = SKILL_FILES.some((f) => f.path === body.to);
    if (exists && !body.overwrite) return jsonResp({ error: 'Destination exists', code: 'CONFLICT' }, 409);
    for (const f of SKILL_FILES) {
      if (f.path === body.from) f.path = body.to;
      else if (f.path.startsWith(`${body.from}/`)) f.path = `${body.to}${f.path.slice(body.from.length)}`;
    }
    return jsonResp({ success: true, data: { path: body.to, contentType: 'text/markdown', sizeBytes: 0, checksum: 'moved' } });
  }

  // POST .../files  → upload (base64). Echo back the ref; patch fixture.
  if (method === 'POST' && url.endsWith('/files')) {
    const body = JSON.parse(typeof init?.body === 'string' ? init.body : '{}') as {
      path?: string;
      contentType?: string;
      data?: string;
    };
    if (!body.path) return jsonResp({ error: 'path required', code: 'BAD_REQUEST' }, 400);
    const sizeBytes = body.data ? Math.floor((body.data.length * 3) / 4) : 0;
    const ref = { path: body.path, contentType: body.contentType ?? 'application/octet-stream', sizeBytes, checksum: `up-${Date.now()}` };
    const idx = SKILL_FILES.findIndex((f) => f.path === body.path);
    if (idx >= 0) SKILL_FILES[idx] = ref;
    else SKILL_FILES.push(ref);
    return jsonResp({ success: true, data: ref }, 201);
  }

  // GET .../files  → list
  if (method === 'GET' && (url.endsWith(base) || url.includes(`${base}?`))) {
    return jsonResp({ success: true, data: SKILL_FILES });
  }

  // DELETE .../files/{path+}
  if (method === 'DELETE') {
    const m = url.match(/\/files\/(.+)$/);
    const path = m ? decodeURIComponent(m[1]) : '';
    let deletedCount = 0;
    for (let i = SKILL_FILES.length - 1; i >= 0; i--) {
      if (SKILL_FILES[i].path === path || SKILL_FILES[i].path.startsWith(`${path}/`)) {
        SKILL_FILES.splice(i, 1);
        deletedCount++;
      }
    }
    return jsonResp({ success: true, data: { deletedCount } });
  }

  // GET .../files/{path+}  → presigned URL (point back at our own stub).
  if (method === 'GET') {
    const m = url.match(/\/files\/(.+)$/);
    const path = m ? decodeURIComponent(m[1]) : '';
    return jsonResp({ success: true, data: { url: `/__skill-blob/${encodeURIComponent(path)}`, filepath: path } });
  }

  return jsonResp({ error: 'Fixture: not wired', code: 'NOT_FOUND' }, 404);
}

// ─── In-memory PlatformEventContext for tests ─────────────────────────
// Subscribers register filter+handler pairs; tests dispatch synthetic
// events via `dispatchTestEvent(...)`. Mirrors the real context's filter
// matching closely enough that vfs.file.* events route to useVfsFiles.

type EventFilter = { eventType?: string; subjectId?: string };
type SubscribedHandler = { filters: EventFilter[]; handler: (e: PlatformEvent) => void };
const testSubscribers = new Set<SubscribedHandler>();

function matchesAny(event: PlatformEvent, filters: EventFilter[]): boolean {
  if (filters.length === 0) return true;
  return filters.some((f) => {
    if (f.eventType && event.eventType !== f.eventType) return false;
    if (f.subjectId && event.subjectId !== f.subjectId) return false;
    return true;
  });
}

function dispatchTestEvent(event: PlatformEvent) {
  for (const sub of testSubscribers) {
    if (matchesAny(event, sub.filters)) sub.handler(event);
  }
}

const stubEventCtx = {
  subscribe: (filters: EventFilter[], handler: (e: PlatformEvent) => void) => {
    const sub: SubscribedHandler = { filters, handler };
    testSubscribers.add(sub);
    return () => { testSubscribers.delete(sub); };
  },
  isConnected: true,
  diagnostics: {
    connectedSince: Date.now(),
    disconnectCount: 0,
    lastDisconnectReason: null,
    lastDisconnectTime: null,
    reconnectAttempts: 0,
  },
};

// Sample bodies served by the fetch stub for selected fixture paths. Keys
// are path strings; values are returned as the response text.
const FIXTURE_BODIES: Record<string, { contentType: string; body: string }> = {
  'reports/q1/data.csv': {
    contentType: 'text/csv',
    body: [
      'region,product,units,revenue',
      'EMEA,Widget,1240,"$12,400.00"',
      'EMEA,Sprocket,815,"$8,150.00"',
      'APAC,Widget,2103,"$21,030.00"',
      'APAC,Sprocket,1678,"$16,780.00"',
      'AMER,Widget,3422,"$34,220.00"',
      'AMER,Sprocket,2901,"$29,010.00"',
      'AMER,"Gadget, premium",612,"$18,360.00"',
    ].join('\n'),
  },
  // Wide CSV (many columns) — reproduces horizontal overflow of the
  // preview pane; verifies the table scrolls inside the pane rather than
  // pushing the canvas/controls off-screen.
  'reports/q1/perf_deltas.csv': {
    contentType: 'text/csv',
    body: [
      'snapshot_time,digest,digest_text,count_star,sum_timer_wait,sum_rows_examined,sum_rows_sent,sum_rows_affected',
      '2026-06-11 12:00:05.123456,aaa111,SELECT * FROM orders WHERE user_id = ?,78,4123000000000,2100,78,0',
      '2026-06-11 12:00:10.250000,aaa111,SELECT * FROM orders WHERE user_id = ?,120,6500000000000,3300,120,0',
      '2026-06-11 12:00:05.123456,bbb222,SELECT name FROM patients WHERE clinic_id = ?,15,700000000000,450,15,0',
      '2026-06-11 12:00:10.250000,bbb222,SELECT name FROM patients WHERE clinic_id = ?,22,980000000000,660,22,0',
    ].join('\n'),
  },
  'config.json': {
    contentType: 'application/json',
    body: JSON.stringify(
      {
        id: 'cfg-01abcdef0123456789',
        version: 3,
        active: true,
        timeoutMs: 30000,
        retries: null,
        tags: ['production', 'us-east-1'],
        owner: { name: 'Mirko', email: 'mirko@example.com' },
      },
      null,
      2,
    ),
  },
  'scripts/import.js': {
    contentType: 'application/x-javascript',
    body: [
      "const { EventBridgeClient, PutEventsCommand } = require('@aws-sdk/client-eventbridge');",
      "const fs = require('fs');",
      '',
      '// Initialize EventBridge client',
      "const client = new EventBridgeClient({ region: 'us-west-2' });",
      '',
      '// Load events from JSON file',
      "const events = JSON.parse(fs.readFileSync('batch2.json', 'utf-8'));",
      '',
      'const eventEntries = events.map((event) => ({',
      '  Source: event.Source,',
      '  DetailType: event.DetailType,',
      '  Detail: event.Detail,',
      '  EventBusName: event.EventBusName,',
      '}));',
      '',
      'const sendEvents = async () => {',
      '  try {',
      '    for (let i = 0; i < eventEntries.length; i += 10) {',
      '      const batch = eventEntries.slice(i, i + 10);',
      '      const command = new PutEventsCommand({ Entries: batch });',
      '      const response = await client.send(command);',
      '      console.log(`Sent ${Math.min(i + 10, eventEntries.length)} of ${eventEntries.length} events`);',
      '    }',
      "  } catch (error) {",
      "    console.error('Error:', error);",
      '  }',
      '};',
      '',
      'sendEvents();',
      '',
    ].join('\n'),
  },
  'scripts/build.sh': {
    contentType: 'application/x-sh',
    body: [
      '#!/usr/bin/env bash',
      'set -euo pipefail',
      '',
      '# Build the frontend bundle and stamp it with the current git sha.',
      'SHA=$(git rev-parse --short HEAD)',
      'echo "Building bundle at sha=$SHA"',
      '',
      'npm ci',
      'VITE_BUILD_SHA="$SHA" npm run build',
      '',
      'echo "Done."',
      '',
    ].join('\n'),
  },
  'scripts/deploy.sh': {
    contentType: 'application/x-sh',
    body: [
      '#!/usr/bin/env bash',
      'set -euo pipefail',
      '',
      'ENV="${1:-stg}"',
      'echo "Deploying to $ENV"',
      '',
      'if [[ "$ENV" == "prd" ]]; then',
      '  AWS_PROFILE=ai-prd npm run cdk:deploy',
      'else',
      '  AWS_PROFILE=ai-stg npm run cdk:deploy',
      'fi',
      '',
    ].join('\n'),
  },
  'scripts/pipeline.py': {
    contentType: 'text/x-python',
    body: [
      'from __future__ import annotations',
      '',
      'import json',
      'from typing import Iterable',
      '',
      '',
      'def chunked(items: Iterable[dict], size: int = 10):',
      '    """Yield successive `size`-sized chunks from `items`."""',
      '    batch: list[dict] = []',
      '    for item in items:',
      '        batch.append(item)',
      '        if len(batch) >= size:',
      '            yield batch',
      '            batch = []',
      '    if batch:',
      '        yield batch',
      '',
      '',
      'if __name__ == "__main__":',
      '    with open("batch2.json") as fh:',
      '        events = json.load(fh)',
      '    for chunk in chunked(events):',
      '        print(f"chunk of {len(chunk)} events")',
      '',
    ].join('\n'),
  },
  'scripts/seed.sql': {
    contentType: 'application/sql',
    body: [
      '-- Seed initial regions + products for the staging DB.',
      'BEGIN;',
      '',
      'INSERT INTO regions (code, name) VALUES',
      "  ('EMEA', 'Europe, Middle East & Africa'),",
      "  ('APAC', 'Asia-Pacific'),",
      "  ('AMER', 'Americas');",
      '',
      'SELECT r.code, COUNT(p.id) AS products',
      'FROM regions r',
      'LEFT JOIN products p ON p.region_code = r.code',
      'WHERE r.code IN (\'EMEA\', \'APAC\')',
      'GROUP BY r.code',
      'ORDER BY products DESC;',
      '',
      'COMMIT;',
      '',
    ].join('\n'),
  },
  'docs/notes.txt': {
    contentType: 'text/plain',
    body: [
      'Notes for the import script:',
      '- batch size capped at 10 by EventBridge PutEvents.',
      '- watch the FailedEntryCount in the response.',
      '- retries are NOT automatic — wrap the loop if you care.',
      '',
    ].join('\n'),
  },
  // JSON-shaped rows for AuiTable.rowsUrl — verifies useVfsContent picks up
  // vfs.file.updated events without waiting on a surface update.
  'data/table-rows.json': {
    contentType: 'application/json',
    body: JSON.stringify(
      [
        { region: 'EMEA', product: 'Widget', units: 1240 },
        { region: 'APAC', product: 'Sprocket', units: 815 },
      ],
      null,
      2,
    ),
  },
};

// ─── fetch override scoped to this page only ─────────────────────────
// Intercepts the VFS list endpoint and answers from the in-memory
// FIXTURE. Serves a small body for paths listed in FIXTURE_BODIES so
// renderers like the CSV tabs can be exercised; returns 404 for any
// other VFS path (download presign / upload / delete).
function installFetchStub() {
  const original = window.fetch.bind(window);
  const handler: typeof window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    // Skill files endpoint (`skillFilesApi.*`) → dedicated skill-files backend.
    const skillResp = skillFilesResponse(url, init);
    if (skillResp) return skillResp;
    // Presigned-blob follow-up (skills readText/download → `fetch(data.url)`).
    const blobMatch = url.match(/\/__skill-blob\/([^?#]+)/);
    if (blobMatch) {
      const path = decodeURIComponent(blobMatch[1]);
      const entry = SKILL_BODIES[path];
      return new Response(entry?.body ?? `// no fixture body for ${path}\n`, {
        status: 200,
        headers: { 'Content-Type': entry?.contentType ?? 'text/plain' },
      });
    }
    // Lazy KB nodes endpoint (`kbApi.listNodes`) → paginated per-folder listing.
    const kbResp = kbNodesResponse(url);
    if (kbResp) return kbResp;
    const match = url.match(/^\/api\/v1\/vfs\/([^/?#]+)(?:[/?]|$)/);
    if (!match) return original(input, init);

    const owner = decodeURIComponent(match[1]);
    if (owner !== FIXTURE_OWNER) return original(input, init);

    // GET /vfs/{owner}  → list
    if (init?.method == null || init.method === 'GET') {
      if (url.endsWith(`/vfs/${owner}`) || url.includes(`/vfs/${owner}?`)) {
        return new Response(JSON.stringify({ data: FIXTURE }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }

      // GET /vfs/{owner}/{path}  → file body (only paths in FIXTURE_BODIES)
      const pathMatch = url.match(/^\/api\/v1\/vfs\/[^/]+\/([^?#]+)/);
      if (pathMatch) {
        const decodedPath = decodeURIComponent(pathMatch[1]);
        const entry = FIXTURE_BODIES[decodedPath];
        if (entry) {
          return new Response(entry.body, {
            status: 200,
            headers: { 'Content-Type': entry.contentType },
          });
        }
      }
    }

    // POST /vfs/{owner}/move → mutate fixture + dispatch vfs.file.moved.
    // Lets the inline-rename and drag-and-drop flows run end-to-end on
    // the dev page without a real backend.
    if (init?.method === 'POST' && url.endsWith(`/vfs/${owner}/move`)) {
      try {
        const body = JSON.parse(typeof init.body === 'string' ? init.body : '{}') as {
          from?: string;
          to?: string;
          overwrite?: boolean;
        };
        if (!body.from || !body.to) {
          return new Response(JSON.stringify({ message: 'from/to required' }), {
            status: 400,
            headers: { 'Content-Type': 'application/json' },
          });
        }
        const target = FIXTURE.find((e) => e.path === body.from);
        if (!target) {
          return new Response(JSON.stringify({ message: 'Not found' }), {
            status: 404,
            headers: { 'Content-Type': 'application/json' },
          });
        }
        const collision = FIXTURE.find((e) => e.path === body.to);
        if (collision && !body.overwrite) {
          return new Response(JSON.stringify({ message: 'Destination exists' }), {
            status: 409,
            headers: { 'Content-Type': 'application/json' },
          });
        }
        // Mutate entry in place; relocate the body fixture too so the
        // serve-by-path code above still finds it.
        const fromPath = target.path;
        const newName = body.to.split('/').pop() ?? body.to;
        target.path = body.to;
        target.name = newName;
        target.updatedAt = new Date().toISOString();
        const body_ = FIXTURE_BODIES[fromPath];
        if (body_) {
          FIXTURE_BODIES[body.to] = body_;
          delete FIXTURE_BODIES[fromPath];
        }
        dispatchTestEvent({
          eventId: `evt-test-${Date.now()}`,
          eventType: 'vfs.file.moved',
          source: 'platform',
          subjectType: 'vfs',
          subjectId: FIXTURE_OWNER,
          data: {
            fileId: target.fileId,
            ownerId: FIXTURE_OWNER,
            fromPath,
            toPath: body.to,
            name: newName,
            size: target.size,
            contentType: target.contentType,
            createdAt: target.createdAt,
            updatedAt: target.updatedAt,
            createdBy: target.createdBy,
            etag: target.etag,
            overwrote: !!collision,
          },
          metadata: { timestamp: new Date().toISOString() },
        } as unknown as PlatformEvent);
        return new Response(JSON.stringify({ ...target }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (err) {
        return new Response(JSON.stringify({ message: String(err) }), {
          status: 400,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    }

    // Everything else (download presign, upload, delete) → 404 for the fixture.
    return new Response(JSON.stringify({ message: 'Fixture: backend not wired' }), {
      status: 404,
      headers: { 'Content-Type': 'application/json' },
    });
  };
  window.fetch = handler;
  return () => { window.fetch = original; };
}

export default function AuiFilesTestPage() {
  const [ready, setReady] = useState(false);
  // Bumps every time the user clicks "Trigger preview" — simulates agent
  // sending a fresh `previewKey` to imperatively open the modal.
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  useEffect(() => {
    const restore = installFetchStub();
    setReady(true);
    return () => { restore(); };
  }, []);

  const nodeMap = useMemo(() => {
    const root: AuiNode = {
      id: 'root',
      component: {
        type: 'Column',
        props: { gap: 6 },
        children: [
          'files-skill',
          'files-kb',
          'files-default',
          'files-list-view',
          'files-readonly',
          'files-agent-preview',
          'table-by-url',
        ],
      },
    };
    const tableByUrl: AuiNode = {
      id: 'table-by-url',
      component: {
        type: 'Table',
        props: {
          title: 'AuiTable from rowsUrl (auto-refreshes on vfs.file.updated)',
          columns: [
            { header: 'Region', key: 'region' },
            { header: 'Product', key: 'product' },
            { header: 'Units', key: 'units' },
          ],
          rowsUrl: `/api/v1/vfs/${FIXTURE_OWNER}/data/table-rows.json`,
        },
      },
    };
    const filesSkill: AuiNode = {
      id: 'files-skill',
      component: {
        type: 'Files',
        props: {
          ownerId: SKILL_OWNER,
          title: 'Skill Files (dedicated /skills/{id}/files backend)',
        },
      },
    };
    const filesKb: AuiNode = {
      id: 'files-kb',
      component: {
        type: 'Files',
        props: {
          ownerId: KB_OWNER,
          title: 'Knowledge Base (lazy, per-folder, paginated)',
        },
      },
    };
    const filesDefault: AuiNode = {
      id: 'files-default',
      // No `title` prop — exercises the owner-type default ("Conversation Files"
      // because FIXTURE_OWNER starts with `con-`).
      component: { type: 'Files', props: { ownerId: FIXTURE_OWNER } },
    };
    const filesList: AuiNode = {
      id: 'files-list-view',
      component: { type: 'Files', props: { ownerId: FIXTURE_OWNER, title: 'List view (no chrome on rows)', initialView: 'list' } },
    };
    const filesReadonly: AuiNode = {
      id: 'files-readonly',
      component: { type: 'Files', props: { ownerId: FIXTURE_OWNER, title: 'Read-only (no upload, no delete)', allowUpload: false, allowDelete: false } },
    };
    const filesAgentPreview: AuiNode = {
      id: 'files-agent-preview',
      component: {
        type: 'Files',
        props: {
          ownerId: FIXTURE_OWNER,
          title: 'Agent preview intent (button bumps previewKey)',
          previewPath: 'docs/spec.md',
          // `previewKey` is injected dynamically below — props rebuild on key change.
          ...(previewKey ? { previewKey } : {}),
        },
      },
    };
    const map = new Map<string, AuiNode>();
    for (const n of [root, filesSkill, filesKb, filesDefault, filesList, filesReadonly, filesAgentPreview, tableByUrl]) map.set(n.id, n);
    return map;
  }, [previewKey]);

  if (!ready) return null;

  return (
    <PlatformEventContext.Provider value={stubEventCtx}>
      <div className="min-h-screen bg-background text-foreground">
        <div className="mx-auto max-w-3xl px-6 py-8">
          <header className="mb-6 border-b border-border pb-4">
            <h1 className="text-2xl font-bold">AUI Files Test</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Renders <code className="bg-muted px-1 py-0.5 rounded">AuiFiles</code>{' '}
              against a static fixture. Backend calls are stubbed (uploads / downloads / deletes fail).
              Dev only.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setPreviewKey(`k-${Date.now()}`)}
                className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
              >
                Trigger preview (docs/spec.md)
              </button>
              <button
                type="button"
                onClick={() => {
                  // Mutate fixture body + dispatch vfs.file.updated. Both the
                  // entry's updatedAt and the served CSV body change, so the
                  // preview should refetch and the table should refresh.
                  const path = 'reports/q1/data.csv';
                  const newBody = [
                    'region,product,units,revenue',
                    `(updated at ${new Date().toLocaleTimeString()})`,
                    'EMEA,Widget,9999,"$99,999.00"',
                  ].join('\n');
                  FIXTURE_BODIES[path] = { contentType: 'text/csv', body: newBody };
                  const target = FIXTURE.find((e) => e.path === path)!;
                  target.size = newBody.length;
                  target.updatedAt = new Date().toISOString();
                  dispatchTestEvent({
                    eventId: `evt-test-${Date.now()}`,
                    eventType: 'vfs.file.updated',
                    source: 'platform',
                    subjectType: 'vfs',
                    subjectId: FIXTURE_OWNER,
                    data: { ...target },
                    metadata: { timestamp: new Date().toISOString() },
                  } as unknown as PlatformEvent);
                }}
                className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
              >
                Simulate update of data.csv
              </button>
              <button
                type="button"
                onClick={() => {
                  // Directly dispatch a synthetic vfs.file.moved (bypasses
                  // vfsApi.move). Verifies the event handler in isolation.
                  const path = 'reports/q1/data.csv';
                  const target = FIXTURE.find((e) => e.path === path);
                  if (!target) return;
                  const newName = `data-${Date.now().toString().slice(-4)}.csv`;
                  const toPath = `reports/q1/${newName}`;
                  const fromPath = target.path;
                  target.path = toPath;
                  target.name = newName;
                  target.updatedAt = new Date().toISOString();
                  const body = FIXTURE_BODIES[fromPath];
                  if (body) {
                    FIXTURE_BODIES[toPath] = body;
                    delete FIXTURE_BODIES[fromPath];
                  }
                  dispatchTestEvent({
                    eventId: `evt-test-${Date.now()}`,
                    eventType: 'vfs.file.moved',
                    source: 'platform',
                    subjectType: 'vfs',
                    subjectId: FIXTURE_OWNER,
                    data: {
                      fileId: target.fileId,
                      ownerId: FIXTURE_OWNER,
                      fromPath,
                      toPath,
                      name: newName,
                      size: target.size,
                      contentType: target.contentType,
                      createdAt: target.createdAt,
                      updatedAt: target.updatedAt,
                      createdBy: target.createdBy,
                      etag: target.etag,
                      overwrote: false,
                    },
                    metadata: { timestamp: new Date().toISOString() },
                  } as unknown as PlatformEvent);
                }}
                className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
              >
                Simulate rename of data.csv
              </button>
              <button
                type="button"
                onClick={() => {
                  // Mutate the JSON rows body + fire vfs.file.updated. The
                  // AuiTable rendered via rowsUrl should refresh without any
                  // surface update.
                  const path = 'data/table-rows.json';
                  const newRows = [
                    { region: 'AMER', product: 'Gadget', units: Math.floor(Math.random() * 5000) },
                    { region: 'EMEA', product: 'Widget', units: Math.floor(Math.random() * 5000) },
                    { region: 'APAC', product: 'Sprocket', units: Math.floor(Math.random() * 5000) },
                  ];
                  const newBody = JSON.stringify(newRows, null, 2);
                  FIXTURE_BODIES[path] = { contentType: 'application/json', body: newBody };
                  dispatchTestEvent({
                    eventId: `evt-test-${Date.now()}`,
                    eventType: 'vfs.file.updated',
                    source: 'platform',
                    subjectType: 'vfs',
                    subjectId: FIXTURE_OWNER,
                    data: {
                      fileId: 'fil-table-rows',
                      ownerId: FIXTURE_OWNER,
                      path,
                      name: 'table-rows.json',
                      size: newBody.length,
                      contentType: 'application/json',
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                      createdBy: 'agent',
                    },
                    metadata: { timestamp: new Date().toISOString() },
                  } as unknown as PlatformEvent);
                }}
                className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
              >
                Simulate update of table-rows.json
              </button>
              <button
                type="button"
                onClick={() => {
                  // Add a fresh file under a deep, previously-uninstantiated
                  // folder. AuiFiles should auto-expand the ancestors so the
                  // new row is visible without manual toggling.
                  const folderId = `dlt-${Date.now().toString().slice(-6)}`;
                  const path = `delegated/${folderId}/sfo_flights.csv`;
                  const body = 'origin,dest\nSFO,JFK\nSFO,LAX\n';
                  FIXTURE_BODIES[path] = { contentType: 'text/csv', body };
                  const entry: VfsEntry = {
                    fileId: `fil-${Date.now()}`,
                    ownerId: FIXTURE_OWNER,
                    path,
                    name: 'sfo_flights.csv',
                    size: body.length,
                    contentType: 'text/csv',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    createdBy: 'agent',
                  };
                  FIXTURE.push(entry);
                  dispatchTestEvent({
                    eventId: `evt-test-${Date.now()}`,
                    eventType: 'vfs.file.created',
                    source: 'platform',
                    subjectType: 'vfs',
                    subjectId: FIXTURE_OWNER,
                    data: { ...entry },
                    metadata: { timestamp: new Date().toISOString() },
                  } as unknown as PlatformEvent);
                }}
                className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
              >
                Create file in collapsed folder
              </button>
              <span className="text-xs text-muted-foreground font-mono">
                previewKey: {previewKey ?? '—'}
              </span>
            </div>
          </header>
          <AuiNodeRenderer nodeId="root" nodeMap={nodeMap} />
        </div>
      </div>
    </PlatformEventContext.Provider>
  );
}
